<template>
  <Loader :show="loading" v-if="loading" />
  <div v-else>
    <MDBRow v-if="promotionsData.length > 0">
      <MDBCol cols="12" md="6" lg="4" class="mb-4 d-flex" v-for="(data, i) in promotionsData" :key="i">
        <Card :btn-class="'btn-primary'" :simple-link="true" :data="data" :linkText="data.linkText" target="_blank" />
      </MDBCol>
    </MDBRow>
    <MDBRow class="mt-4">
      <MDBCol lg="8">
        <div class="bg-white px-4 py-4 custom-card">
          <h2 class="title font-28 fw-bold">
            {{ $t("salesPromotions.participation.title") }}
          </h2>
          <div class="font-16" v-html="$t('salesPromotions.participation.desc')"></div>
          <div class="my-4" v-if="promotionsData.length > 0">
            <template v-for="(value, name) in promotionsData" :key="`promotion_${name}`">
              <MDBRow class="font-16">
                <MDBCol lg="1">
                  <MDBSwitch v-model="promo[promotionKey(value)]" :disabled="blockPromo[promotionKey(value)]" />
                </MDBCol>
                <MDBCol lg="11">
                  <span v-html="value.subtitle" class="fw-bold text-uppercase"></span> - <span
                    v-html="value.title"></span>
                </MDBCol>
              </MDBRow>
            </template>
          </div>
          <MDBRow>
            <MDBCol lg="6">
              <MDBBtn class="btn btn-danger text-uppercase align-self-start" :disabled="allPromotions || isSubmitting"
                @click="saveUserPromotions">
                {{ $t("salesPromotions.participation.request") }}
              </MDBBtn>
            </MDBCol>
            <MDBCol v-if="success" lg="6" class="d-flex align-items-center">
              {{ $t("salesPromotions.participation.success") }}
            </MDBCol>
          </MDBRow>
        </div>
      </MDBCol>
    </MDBRow>
  </div>

</template>

<script setup>
import { MDBRow, MDBCol, MDBSwitch, MDBBtn } from "mdb-vue-ui-kit";
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useI18n } from 'vue-i18n';

import { UserService } from "@/services/user.service";
import Card from "@/components/General/Card/index.vue";
import Loader from "@/components/General/Loader/index.vue";

const store = useStore();

const promo = ref({})
const blockPromo = ref({})
const success = ref(false);
const isSubmitting = ref(false);
const loading = ref(true)
const { t } = useI18n();

const me = computed(() => store.getters.getMe);
const userPromotions = computed(() => store.getters.getUserPromotions);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const rawPromotionsData = computed(() => store.getters.getPromotions);

const getLocalizedValue = (localizedObject) => {
  if (!localizedObject || typeof localizedObject !== "object") return "";

  const locale = currentLocale.value;
  if (locale && localizedObject[locale]) return localizedObject[locale];

  const languagePart = locale?.split("-")[0];
  if (languagePart) {
    const localeByLanguage = Object.keys(localizedObject).find(
      (key) => key?.split("-")[0] === languagePart
    );
    if (localeByLanguage && localizedObject[localeByLanguage]) {
      return localizedObject[localeByLanguage];
    }
  }

  const firstAvailableLocale = Object.keys(localizedObject)[0];
  return firstAvailableLocale ? localizedObject[firstAvailableLocale] : "";
};

const promotionsData = computed(() => {
  if (!Array.isArray(rawPromotionsData.value)) return [];

  return rawPromotionsData.value.map((item) => {
    const title = getLocalizedValue(item?.title);
    const subtitle = getLocalizedValue(item?.subtitle);

    return {
      ...item,
      title,
      subtitle,
      description: getLocalizedValue(item?.description),
      link: getLocalizedValue(item?.link),
      image: getLocalizedValue(item?.image),
      linkText: t("global.buttons.dealerInfoDownload"),
      name: `<span class="font-sm">${title}</span><br/><h3 class="fw-bold text-uppercase">${subtitle}</h3>`,
    };
  });
});

const allPromotions = computed(() => {
  const values = Object.values(blockPromo.value);
  return values.length > 0 && values.every(Boolean);
});

const promotionKey = (promotion) => {
  return String(promotion?.plainId ?? promotion?.id ?? "");
};

watch(
  [promotionsData, userPromotions],
  ([promotions, joinedPromotions]) => {
    if (!Array.isArray(promotions) || promotions.length === 0) return;

    const joinedIds = new Set(
      (Array.isArray(joinedPromotions) ? joinedPromotions : [])
        .map((item) => String(item?.promotion_id ?? item?.plainId ?? item?.id ?? ""))
        .filter(Boolean)
    );

    const nextPromo = {};
    const nextBlockPromo = {};

    for (const promotion of promotions) {
      const key = promotionKey(promotion);
      if (!key) continue;

      const isJoined = joinedIds.has(key);
      nextPromo[key] = isJoined;
      nextBlockPromo[key] = isJoined;
    }

    promo.value = nextPromo;
    blockPromo.value = nextBlockPromo;
  },
  { immediate: true }
);

const saveUserPromotions = async () => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;

  let args = {
    user_id: me.value.plainId,
    promotions: promo.value,
  };

  try {
    const response = await UserService.saveUserPromotions(args);
    if (response.status == 200) {
      toast(t("toast.requestSent"), {
        type: "success",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      (success.value = true), await store.dispatch("loadUserPromotions");
      setTimeout(() => {
        success.value = false;
      }, 5000);
    }
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  await store.dispatch("fetchPromotions");
  await store.dispatch("loadUserPromotions");
  await store.dispatch("me");
  loading.value = false
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

:deep(.font-sm) {
  font-size: 20px;
}

:deep(.custom-card) {
  display: flex;
  flex-direction: column;
}

:deep(.training-description) {
  margin-bottom: auto;
}

:deep(.form-switch .form-check-input:checked[type="checkbox"]),
:deep(.form-switch .form-check-input:checked[type="checkbox"]::after) {
  background-color: #ed1c24;
}

:deep(.form-switch .form-check-input:checked:focus::before) {
  box-shadow: 3px -1px 0px 13px #ed1c24;
}

.title {
  position: relative;

  &::before {
    content: "";
    display: inline-block;
    width: 28px;
    height: 4px;
    background: #ed1c24;
    transform: rotate(110deg);
    margin: 10px 0;
  }

  @media screen and (max-width: $mdBreakpoint) {
    font-size: 20px;
  }
}
</style>
